import React, { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { DRIVE_TAB } from "@/utils/constants/tabConstants";
import { FilterValue } from "../../components";
import { Video, Music, Film, Filter as FilterIcon, TvIcon } from "lucide-react";
import { Tooltip } from "../../components";
import { Dropdown } from "react-bootstrap";
import { useScrollOutside } from "../../hooks/useScrollOutside";
import { getTrackUserContext, trackEvent } from "@/utils/analytics";
import { getContentLocation } from "@/utils/utilFunctions/commmonFunctions";
import { store } from "@/store";

type TopFiltersProps = {
  limit?: number;
  activeType: string;
  isNavTabsDisabled: boolean;
  filters: Record<string, FilterValue>;
  onTabChange: (key: string) => void;
  onSortChange: (value: string) => void;
  showAdvancedFilters?: boolean;
  onToggleAdvancedFilters?: () => void;
};

const navTabs = [
  { label: "Video", key: "video", icon: Video },
  { label: "Audio", key: "audio", icon: Music },
  { label: "Shorts", key: "shorts", icon: Film },
  { label: "Premiere", key: "premiere", icon: TvIcon },
];

const driveTabs = [
  { label: "Video", key: "drive-video", icon: Video, path: "video" },
  { label: "Shorts", key: "drive-shorts", icon: Film, path: "shorts" },
];

const sortOptions = [
  { label: "Recently Uploaded", value: "recent" },
  { label: "Oldest First", value: "oldest" },
  { label: "Longest First", value: "longest" },
  { label: "Shortest First", value: "shortest" },
];

const TopFilters: React.FC<TopFiltersProps> = ({
  limit,
  activeType,
  isNavTabsDisabled,
  filters,
  onTabChange,
  onSortChange,
  showAdvancedFilters,
  onToggleAdvancedFilters,
}) => {
  const analyticsLocation = getContentLocation();
  const location = useLocation();
  const activeDrivePath = location.pathname.split("/").pop();

  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState<boolean>(false);
  const sortDropdownRef = useRef<HTMLDivElement>(null);

  useScrollOutside({
    isOpen: isSortDropdownOpen,
    onClose: () => setIsSortDropdownOpen(false),
    enabled: true,
    ref: sortDropdownRef,
  });

  const selectedSortLabel =
    sortOptions.find(
      (opt) => opt.value === (filters as Record<string, FilterValue>)?.sortBy
    )?.label ?? "Recently Uploaded";

  if (limit) {
    return null;
  }

  return (
    <div className="subHeader d-flex justify-content-between align-items-center p-3 z-4 gap-3">
      <ul className="nav nav-underline gap-3 mb-0 mt-3 flex-shrink-0">
        {activeType === DRIVE_TAB
          ? driveTabs.map((item) => (
              <li key={item.key} className="nav-item">
                <a
                  className={`nav-link px-2 py-2 gap-1 f-14 lh-14 ${
                    activeDrivePath === item.path ? "active" : ""
                  } ${isNavTabsDisabled ? "disabled" : ""}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isNavTabsDisabled && activeDrivePath !== item.path) {
                      trackEvent("browse", {
                        category: "filters",
                        entity: item.label,
                        action: "click",
                        location: analyticsLocation,
                        ...getTrackUserContext(),
                      });
                      onTabChange(item.key);
                    }
                  }}
                  style={{
                    pointerEvents: isNavTabsDisabled ? "none" : "auto",
                    opacity: isNavTabsDisabled ? 0.5 : 1,
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))
          : navTabs.map((item) => (
              <li key={item.key} className="nav-item">
                <a
                  className={`nav-link px-2 py-2 gap-1 f-14 lh-14 ${
                    activeType === item.key ? "active" : ""
                  } ${isNavTabsDisabled ? "disabled" : ""}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    if (!isNavTabsDisabled) {
                      trackEvent("browse", {
                        category: "filters",
                        entity: item.label,
                        action: "click",
                        location: analyticsLocation,
                        ...getTrackUserContext(),
                      });
                      onTabChange(item.key);
                    }
                  }}
                  style={{
                    pointerEvents: isNavTabsDisabled ? "none" : "auto",
                    opacity: isNavTabsDisabled ? 0.5 : 1,
                  }}
                >
                  {item.label}
                </a>
              </li>
            ))}
      </ul>
      <div className="flex-grow-1" />
      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        <div className="sort-by">
          <label className="visually-hidden">Sort by</label>
          <Dropdown
            ref={sortDropdownRef}
            key={`sort-dropdown-${isSortDropdownOpen}`}
            show={isSortDropdownOpen}
            onToggle={(isOpen) => setIsSortDropdownOpen(isOpen)}
            onSelect={(value) => {
              if (value) {
                const selected = sortOptions.find(
                  (opt) => opt.value === value.toString()
                );
                trackEvent("browse", {
                  category: "filters",
                  entity: "Sortby",
                  action: selected?.label || value.toString(),
                  location: analyticsLocation,
                  ...getTrackUserContext(),
                });
                onSortChange(value.toString());
              }
              setIsSortDropdownOpen(false);
            }}
            className="d-inline-flex"
          >
            <Dropdown.Toggle
              style={{ minWidth: "140px" }}
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center gap-1"
              id="sort-by-dropdown"
            >
              <span className="text-capitalize">{selectedSortLabel}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {sortOptions.map((opt) => {
                const isActive =
                  (filters as Record<string, FilterValue>)?.sortBy ===
                    opt.value ||
                  (!(filters as Record<string, FilterValue>)?.sortBy &&
                    opt.value === "recent");
                return (
                  <Dropdown.Item
                    key={opt.value}
                    eventKey={opt.value}
                    active={isActive}
                    className={isActive ? "active" : ""}
                  >
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <span>{opt.label}</span>
                      {isActive && <span className="text-success ms-2">✓</span>}
                    </div>
                  </Dropdown.Item>
                );
              })}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        {onToggleAdvancedFilters && (
          <div className="d-flex align-items-center gap-1">
            <Tooltip content="Advance Filters" placement="bottom">
              <button
                type="button"
                className={`btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 ${
                  showAdvancedFilters ? "active" : ""
                }`}
                onClick={onToggleAdvancedFilters}
                aria-label="Advance Search"
              >
                <FilterIcon size={16} />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default TopFilters;
